import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { commentApi, CreateCommentDTO, UpdateCommentDTO } from '../../../../../apis/comment.api';
import { useAtomValue } from 'jotai';
import { profileAtom } from '../../../../Settings/atom';
import { useEffect } from 'react';
import { debounce } from 'lodash';

export const useComments = (
  articleId: string,
  isGeneral?: boolean,
  nodeId?: string,
  sortValue?: string,
  limit: number = 10,
  page: number = 1,
  notificationCommentId?: string,
  notificationRepliedCommentId?: string
) => {
  const profile = useAtomValue(profileAtom);
  const queryClient = useQueryClient();

  const {
    data: comments = [] as any,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey: ['comments', articleId, nodeId, limit, profile.id, sortValue, notificationCommentId],
    queryFn: ({ pageParam = 1 }) =>
      sortValue === 'mostRelevant'
        ? commentApi.getRelevantComments(articleId, nodeId, false, limit, pageParam, profile.id)
        : commentApi.getComments(
            articleId,
            nodeId,
            false,
            limit,
            pageParam,
            profile.id,
            notificationCommentId
          ),
    initialPageParam: 1, // Start with page 1
    getNextPageParam: (lastPage, allPages) => {
      // Check if there are more pages
      return lastPage.data.length < limit ? undefined : allPages.length + 1;
    },
    enabled: !!nodeId && !!articleId
  });

  const {
    data: generalComments = [] as any,
    isLoading: generalCommentsLoading,
    error: generalCommentsError,
    fetchNextPage: fetchNextPageGeneralComments,
    hasNextPage: hasNextPageGeneralComments,
    isFetchingNextPage: isFetchingNextPageGeneralComments,
    refetch: refetchGeneralComments
  } = useInfiniteQuery({
    queryKey: ['generalComments', articleId, limit, profile.id, sortValue, notificationCommentId], // Remove 'page' as it's managed internally
    queryFn: ({ pageParam = 1 }) =>
      sortValue === 'mostRelevant'
        ? commentApi.getRelevantComments(articleId, null, isGeneral, limit, pageParam, profile.id)
        : commentApi.getComments(
            articleId,
            null,
            isGeneral,
            limit,
            pageParam,
            profile.id,
            notificationCommentId
          ),
    initialPageParam: 1, // Start with page 1
    getNextPageParam: (lastPage, allPages) => {
      // Check if there are more pages
      return lastPage.data.length < limit ? undefined : allPages.length + 1;
    }
  });

  const createCommentMutation = useMutation<any, Error, CreateCommentDTO & { nodeId: string }>({
    mutationFn: commentApi.createComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['create', articleId, nodeId]
      });
    }
  });

  const createGeneralCommentMutation = useMutation<any, Error, any>({
    mutationFn: commentApi.createGeneralComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['create', articleId]
      });
    }
  });

  const updateCommentMutation = useMutation<any, Error, UpdateCommentDTO>({
    mutationFn: commentApi.updateComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['update']
      });
    }
  });

  const deleteCommentMutation = useMutation<any, Error, string>({
    mutationFn: commentApi.deleteComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['delete']
      });
    }
  });

  const reactToCommentMutation = useMutation<
    any, // Replace with proper reaction response type
    Error,
    { commentId: string; nodeId: string; reactionType: string; isLike: boolean }
  >({
    mutationFn: commentApi.reactToComment,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['react']
      });
    }
  });

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight === e.target.scrollTop + e.target.clientHeight;
    if (bottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleScrollGeneral = debounce(() => {
    const scrollPosition = window.scrollY + window.innerHeight;
    const scrollThreshold = document.documentElement.scrollHeight - 1000;

    const nearBottom = scrollPosition >= scrollThreshold;

    if (nearBottom && hasNextPageGeneralComments && !isFetchingNextPageGeneralComments) {
      fetchNextPageGeneralComments();
    }
  }, 200);

  useEffect(() => {
    window.addEventListener('scroll', handleScrollGeneral);

    return () => {
      window.removeEventListener('scroll', handleScrollGeneral);
      handleScrollGeneral.cancel();
    };
  }, [hasNextPageGeneralComments, isFetchingNextPageGeneralComments, fetchNextPageGeneralComments]);

  return {
    comments: comments,
    isLoading: isLoading,
    generalComments: generalComments,
    generalCommentsLoading: generalCommentsLoading,
    error,
    createComment: createCommentMutation.mutateAsync, // Changed to mutateAsync
    createGeneralComment: createGeneralCommentMutation.mutateAsync,
    isCreatingComment: createCommentMutation.isPending,
    isCreatingGeneralComment: createGeneralCommentMutation.isPending,
    updateComment: updateCommentMutation.mutateAsync, // Changed to mutateAsync
    isUpdatingComment: updateCommentMutation.isPending,
    deleteComment: deleteCommentMutation.mutateAsync,
    reactToComment: reactToCommentMutation.mutateAsync, // Changed to mutateAsync
    isReacting: reactToCommentMutation.isPending,
    refetchComments: refetch,
    refetchGeneralComments,
    handleScroll,
    handleScrollGeneral,
    isFetchingNextPage,
    isFetchingNextPageGeneralComments
  };
};
